import { type ReactNode, useState } from 'react'
import { Sidebar } from './Sidebar'
import { MobileNav } from './MobileNav'
import { MobileHeader } from './MobileHeader'
import { CrisisTopbar } from '../../features/crisis/components'
import { useCrisisStore } from '../../lib/stores/crisisStore'
import { useAuth } from '../../lib/hooks/useAuth'
import { useIsMobileNav } from '../../lib/hooks/useMediaQuery'

interface LayoutProps {
  children: ReactNode
  showNavigation?: boolean
  showCrisisTopbar?: boolean
  className?: string
  removeBottomPadding?: boolean
  fullHeight?: boolean
}

export function Layout({
  children,
  showNavigation = true,
  showCrisisTopbar = true,
  className = '',
  removeBottomPadding = false,
  fullHeight = true,
}: LayoutProps) {
  const { activeCrisis } = useCrisisStore()
  const { isAuthenticated } = useAuth()
  const isMobile = useIsMobileNav()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [sidebarMinimized, setSidebarMinimized] = useState(false)

  const hasTopbar = isAuthenticated && activeCrisis && showCrisisTopbar

  // Responsive padding: smaller on mobile
  const mainPaddingClass = removeBottomPadding ? 'px-2 pt-4 pb-0 md:px-6 md:pt-6' : 'p-2 md:p-6'

  return (
    <div className={`flex flex-col h-screen gradient-background ${className}`}>
      {hasTopbar && <CrisisTopbar crisis={activeCrisis} />}

      {showNavigation ? (
        isMobile ? (
          // Mobile Layout
          <>
            <MobileHeader onMenuClick={() => setMobileNavOpen(true)} />
            <MobileNav isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
            <main
              id="main-content"
              className={`content-surface ${fullHeight ? 'flex-1' : ''} overflow-y-auto`}
              role="main"
              tabIndex={-1}
            >
              <div className={mainPaddingClass}>{children}</div>
            </main>
          </>
        ) : (
          // Desktop Layout
          <div className="app-shell flex-1" style={{ '--sidebar-width': sidebarMinimized ? '4rem' : '16rem' } as React.CSSProperties}>
            <Sidebar minimized={sidebarMinimized} onToggleMinimize={() => setSidebarMinimized(!sidebarMinimized)} />
            <main
              id="main-content"
              className={`content-surface main-content ${fullHeight ? '' : 'min-h-0'}`}
              role="main"
              tabIndex={-1}
            >
              <div className={mainPaddingClass}>{children}</div>
            </main>
          </div>
        )
      ) : (
        <main
          id="main-content"
          className={`content-surface main-content ${fullHeight ? 'flex-1' : ''} ${mainPaddingClass}`}
          role="main"
          tabIndex={-1}
        >
          {children}
        </main>
      )}
    </div>
  )
}
